/**
 * Copyright 2023 The Ground Authors.
 *
 * Licensed under the Apache License, Version 2.0 (the 'License');
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an 'AS IS' BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { List } from 'immutable';
import { Subscription } from 'rxjs';

import { LoiEditorComponent } from 'app/components/shared/loi-editor/loi-editor.component';
import { TaskEditorComponent } from 'app/components/shared/task-editor/task-editor.component';
import { DataCollectionStrategy, Job } from 'app/models/job.model';
import { LocationOfInterest } from 'app/models/loi.model';
import { Task } from 'app/models/task/task.model';
import { DraftSurveyService } from 'app/services/draft-survey/draft-survey.service';
import { LocationOfInterestService } from 'app/services/loi/loi.service';
import { NavigationService } from 'app/services/navigation/navigation.service';
import { SurveyService } from 'app/services/survey/survey.service';
import { TaskService } from 'app/services/task/task.service';

enum EditJobSection {
  TASKS,
  LOIS,
}

@Component({
  selector: 'edit-job',
  templateUrl: './edit-job.component.html',
  styleUrls: ['./edit-job.component.scss'],
  standalone: false,
})
export class EditJobComponent {
  subscription: Subscription = new Subscription();
  loisSubscription: Subscription = new Subscription();

  surveyId?: string;
  jobId?: string;

  section: EditJobSection = EditJobSection.TASKS;

  job?: Job;
  tasks?: List<Task>;
  addLoiTaskId?: string;
  typeLockedTaskIds: ReadonlySet<string> = new Set();
  lois!: List<LocationOfInterest>;

  EditJobSection = EditJobSection;

  @ViewChild('tasksEditor')
  tasksEditor?: TaskEditorComponent;

  @ViewChild('loiEditor')
  loiEditor?: LoiEditorComponent;

  constructor(
    private route: ActivatedRoute,
    private navigationService: NavigationService,
    private loiService: LocationOfInterestService,
    private taskService: TaskService,
    public surveyService: SurveyService,
    public draftSurveyService: DraftSurveyService
  ) {
    this.subscription.add(
      this.navigationService
        .getSurveyId$()
        .subscribe(surveyId => this.onSurveyIdChange(surveyId))
    );
  }

  async ngOnInit(): Promise<void> {
    this.subscription.add(
      this.route.params.subscribe(async params => {
        await this.onJobIdChange(params);
      })
    );
  }

  private onSurveyIdChange(surveyId: string | null) {
    if (surveyId) {
      this.surveyId = surveyId;
    }
  }

  private onJobIdChange(params: Params) {
    this.jobId = params['id'];

    this.job = this.draftSurveyService.getSurvey().getJob(this.jobId!);

    this.addLoiTaskId = this.job?.tasks?.find(task => !!task.addLoiTask)?.id;

    this.typeLockedTaskIds = this.draftSurveyService.getTypeLockedTaskIds(
      this.jobId!
    );

    if (!this.job) return;

    this.loisSubscription.unsubscribe();
    this.loisSubscription = new Subscription();
    this.loisSubscription.add(
      this.loiService
        .getPredefinedLoisByJobId$(
          this.draftSurveyService.getSurvey(),
          this.job!.id
        )
        .subscribe((lois: List<LocationOfInterest>) => (this.lois = lois))
    );

    this.tasks = this.job?.tasks?.toList().sortBy(task => task.index);
  }

  onSectionChange(section: EditJobSection) {
    if (this.tasksEditor && section === EditJobSection.LOIS) {
      this.tasks = this.tasksEditor.toTasks();
    }
    this.section = section;
  }

  onTasksChange(valid: boolean): void {
    if (this.jobId && this.tasksEditor) {
      this.draftSurveyService.addOrUpdateTasks(
        this.jobId,
        this.tasksEditor.toTasks(),
        valid
      );
    }
  }

  onStrategyChange(strategy: DataCollectionStrategy) {
    const job = this.draftSurveyService.getSurvey().getJob(this.jobId!);

    if (!job) return;

    const addLoiTask = job.tasks?.find(task => !!task.addLoiTask);

    if (addLoiTask) this.addLoiTaskId = addLoiTask.id;

    const tasks = this.taskService.updateLoiTasks(
      job.tasks,
      strategy,
      this.addLoiTaskId
    );

    this.draftSurveyService.addOrUpdateJob(job.copyWith({ tasks, strategy }));

    this.job = this.draftSurveyService.getSurvey().getJob(this.jobId!);

    this.tasks = tasks?.toList().sortBy(task => task.index);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();

    this.loisSubscription.unsubscribe();
  }
}
